import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { cancelSubscriptionInput, subscribeCustomerInput } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import { formatInTimeZone } from 'date-fns-tz';
import { SUB_STATUS, useActivePlans, type Subscription } from './api';
import { applyServerError, fmtIsoDate, zodForm } from './lib';

function SubscribeModal({ customerId, open, onOpenChange }: { customerId: string; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const tz = useTimezone();
  const plans = useActivePlans(open);
  interface Values {
    planId: string;
    startsOn: string;
  }
  const form = useForm<Values>({
    resolver: zodForm<Values>(subscribeCustomerInput),
    values: { planId: '', startsOn: formatInTimeZone(new Date(), tz, 'yyyy-MM-dd') },
  });
  const { errors } = form.formState;
  const save = useMutation({
    mutationFn: (v: Values) => api.post<Subscription>(`/customers/${customerId}/subscriptions`, subscribeCustomerInput.parse(v)),
    onSuccess: () => {
      toast.success('Assinatura criada. Falta registrar o pagamento no caixa.');
      qc.invalidateQueries({ queryKey: ['customers'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e, { planId: 'planId' }),
  });
  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Assinar plano ou pacote"
      description="A assinatura só vale depois que o pagamento for registrado no caixa."
      footer={
        <>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="subscribe-form" disabled={save.isPending}>
            {save.isPending ? 'Criando…' : 'Criar assinatura'}
          </Button>
        </>
      }
    >
      <form id="subscribe-form" className="grid gap-4" noValidate onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <Field id="sub-plan" label="Plano ou pacote" error={errors.planId?.message}>
          <Controller
            control={form.control}
            name="planId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="sub-plan" className="w-full">
                  <SelectValue placeholder={plans.isLoading ? 'Carregando…' : 'Escolha um plano'} />
                </SelectTrigger>
                <SelectContent>
                  {plans.data?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {money(p.priceCents)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {plans.data?.length === 0 && <p className="text-xs text-muted-foreground">Nenhum plano ativo. Cadastre em Fidelidade e planos.</p>}
        </Field>
        <Field id="sub-start" label="Início previsto" error={errors.startsOn?.message}>
          <Input id="sub-start" type="date" {...form.register('startsOn')} />
        </Field>
      </form>
    </FormModal>
  );
}

function CancelModal({ customerId, sub, onClose }: { customerId: string; sub: Subscription | null; onClose: () => void }) {
  const qc = useQueryClient();
  const form = useForm<{ reason: string }>({ resolver: zodForm(cancelSubscriptionInput), defaultValues: { reason: '' } });
  const save = useMutation({
    mutationFn: (v: { reason: string }) => api.post(`/customers/${customerId}/subscriptions/${sub!.id}/cancel`, v),
    onSuccess: () => {
      toast.success('Assinatura cancelada');
      qc.invalidateQueries({ queryKey: ['customers'] });
      form.reset();
      onClose();
    },
    onError: (e) => applyServerError(form, e),
  });
  return (
    <FormModal
      open={!!sub}
      onOpenChange={(v) => !v && onClose()}
      size="sm"
      title="Cancelar assinatura"
      description={sub ? `${sub.planName}: o cliente perde os usos restantes. Esta ação fica na auditoria.` : undefined}
      footer={
        <>
          <Button variant="outline" type="button" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" form="cancel-sub-form" variant="destructive" disabled={save.isPending}>
            {save.isPending ? 'Cancelando…' : 'Cancelar assinatura'}
          </Button>
        </>
      }
    >
      <form id="cancel-sub-form" className="grid gap-4" noValidate onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <Field id="cancel-reason" label="Motivo" error={form.formState.errors.reason?.message}>
          <Input id="cancel-reason" maxLength={200} {...form.register('reason')} />
        </Field>
      </form>
    </FormModal>
  );
}

export function CustomerSubscriptionsTab({ customerId, disabled }: { customerId: string; disabled?: boolean }) {
  const { can } = useSession();
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState<Subscription | null>(null);
  const q = useQuery({ queryKey: ['customers', 'subscriptions', customerId], queryFn: () => api.get<Subscription[]>(`/customers/${customerId}/subscriptions`) });

  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Planos recorrentes e pacotes de serviços deste cliente.</p>
        {can('customers.write') && !disabled && (
          <Button onClick={() => setOpen(true)}>
            <Plus aria-hidden /> Assinar plano
          </Button>
        )}
      </div>
      {q.data.length === 0 ? (
        <EmptyState title="Nenhuma assinatura" description="Venda um plano mensal ou um pacote de cortes para este cliente." />
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {q.data.map((s) => (
            <li key={s.id} className="grid gap-2 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-medium">{s.planName}</span>
                <Badge variant={s.status === 'active' ? 'secondary' : 'outline'}>{SUB_STATUS[s.status]}</Badge>
                <span className="tabular text-muted-foreground">{money(s.priceCents)}</span>
                <span className="ml-auto flex items-center gap-1">
                  {s.status === 'pending_payment' && can('cash.operate') && (
                    <Button asChild size="sm">
                      <Link to={`/app/caixa?assinatura=${s.id}`}>Registrar pagamento</Link>
                    </Button>
                  )}
                  {(s.status === 'active' || s.status === 'pending_payment') && can('loyalty.manage') && (
                    <Button variant="ghost" size="sm" onClick={() => setCancelling(s)}>
                      Cancelar
                    </Button>
                  )}
                </span>
              </div>
              {s.status === 'pending_payment' ? (
                <p className="text-muted-foreground">
                  Aguardando pagamento: registre o recebimento no caixa para ativar.{' '}
                  <Link to={`/app/caixa?assinatura=${s.id}`} className="underline">
                    Abrir no caixa
                  </Link>
                </p>
              ) : (
                <p className="tabular text-muted-foreground">
                  Período: {fmtIsoDate(s.currentPeriodStart)} a {fmtIsoDate(s.currentPeriodEnd)}
                  {s.usesRemaining != null ? ` · ${s.usesRemaining} de ${s.usesIncluded ?? s.usesRemaining} usos restantes` : s.planKind === 'recurring' ? ' · usos ilimitados' : ''}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      <SubscribeModal customerId={customerId} open={open} onOpenChange={setOpen} />
      <CancelModal customerId={customerId} sub={cancelling} onClose={() => setCancelling(null)} />
    </div>
  );
}
